/**
 * Game Plan Tracker — Live Performance Dashboard
 *
 * A single-page live "business command centre": all KPIs, pipeline breakdown,
 * leaderboards, trend chart, files-requiring-attention list and recent activity
 * for either This Week or This Month (or a custom range).
 *
 * Data source: GET /api/gameplan/live-dashboard
 * Refreshes every 60s automatically while mounted.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight, Calendar,
  ChevronRight, ClipboardCheck, FileCheck, FilePlus, Filter, Loader2, Plus,
  Printer, RefreshCw, Send, Target, TrendingUp, Trophy, Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import { backendUrl, formatCurrency, formatCompactCurrency } from './gameplanUtils';
import QuickEntryDialog, { QUICK_ENTRY_EVENTS } from './QuickEntryDialog';
import GoalsProgressWidget from './GoalsProgressWidget';

const VIEW_LABEL = { week: 'This Week', month: 'This Month', custom: 'Custom Range' };
const SCOPE_LABEL = { business: 'Whole business', pod: 'My pod', personal: 'My results' };

// ── Small primitives ─────────────────────────────────────────────
const fmtInt = (n) => (n ?? 0).toLocaleString('en-AU');
const fmtPct = (n) => (n === null || n === undefined) ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

const DeltaChip = ({ pct, invert }) => {
  if (pct === null || pct === undefined) return <span className="text-[10px] text-slate-400">no baseline</span>;
  const positive = invert ? pct < 0 : pct >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  const tone = positive
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-800'
    : 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-800';
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full ring-1 ring-inset', tone)}>
      <Icon className="w-3 h-3" /> {fmtPct(pct)}
    </span>
  );
};

const ProgressBar = ({ pct, tone = 'blue' }) => {
  const clamped = Math.min(100, Math.max(0, pct || 0));
  const toneCls = {
    blue: 'from-sky-400 to-blue-500',
    emerald: 'from-emerald-400 to-emerald-600',
    amber: 'from-amber-400 to-orange-500',
    violet: 'from-violet-400 to-purple-500',
  }[tone] || 'from-sky-400 to-blue-500';
  return (
    <div className="h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
      <div className={cn('h-full bg-gradient-to-r rounded-full transition-all duration-500', toneCls)}
           style={{ width: `${clamped}%` }} />
    </div>
  );
};

// ── KPI Card ─────────────────────────────────────────────────────
const KpiCard = ({ title, primary, secondary, deltaPct, target, achievementPct,
                   tone = 'slate', icon: Icon, manualCount = 0, manualLabel = 'logged' }) => {
  const toneRing = {
    slate:   'from-slate-500/8 to-slate-500/0',
    blue:    'from-sky-400/12 to-sky-400/0',
    emerald: 'from-emerald-400/12 to-emerald-400/0',
    amber:   'from-amber-400/12 to-amber-400/0',
    rose:    'from-rose-400/12 to-rose-400/0',
    violet:  'from-violet-400/12 to-violet-400/0',
  }[tone] || 'from-slate-500/8 to-slate-500/0';
  return (
    <div className="relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4 overflow-hidden">
      <div className={cn('absolute inset-0 bg-gradient-to-br pointer-events-none', toneRing)} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400">
            {Icon && <Icon className="w-3.5 h-3.5" />} {title}
          </div>
          {deltaPct !== undefined && <DeltaChip pct={deltaPct} />}
        </div>
        <div className="mt-2 text-2xl sm:text-[26px] font-black tabular-nums leading-none text-slate-900 dark:text-white">
          {primary}
        </div>
        {secondary && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1 tabular-nums">{secondary}</div>}
        {manualCount > 0 && (
          <div
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider mt-1.5
                       px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200
                       dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-800"
            title={`${manualCount} entered via Quick Entry`}
            data-testid="kpi-manual-chip"
          >
            <Plus className="w-2.5 h-2.5" />
            {manualCount} {manualLabel}
          </div>
        )}
        {target !== undefined && target !== null && target > 0 && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-[10.5px] font-semibold text-slate-500 dark:text-slate-400">
              <span>Target {fmtInt(target)}</span>
              <span className={cn(
                'font-bold',
                achievementPct >= 100 ? 'text-emerald-600 dark:text-emerald-400'
                  : achievementPct >= 70 ? 'text-amber-600 dark:text-amber-400'
                    : 'text-rose-600 dark:text-rose-400'
              )}>{Math.round(achievementPct || 0)}%</span>
            </div>
            <div className="mt-1">
              <ProgressBar pct={achievementPct} tone={achievementPct >= 100 ? 'emerald' : achievementPct >= 70 ? 'amber' : 'blue'} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Trend mini-chart (SVG bars) ─────────────────────────────────
const TrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => d.lodged || 0));
  const width = 100;
  const barW = width / data.length;
  return (
    <div>
      <svg viewBox={`0 0 ${width} 32`} preserveAspectRatio="none" className="w-full h-16">
        {data.map((d, i) => {
          const h = ((d.lodged || 0) / max) * 30;
          return (
            <g key={d.date}>
              <rect x={i * barW + 0.5} y={32 - h} width={Math.max(0, barW - 1)} height={h}
                    className="fill-sky-400 dark:fill-sky-500" rx="0.4" />
              {(d.approved || 0) > 0 && (
                <rect x={i * barW + 0.5} y={32 - h - ((d.approved / max) * 6) - 1}
                      width={Math.max(0, barW - 1)} height={Math.max(0.6, (d.approved / max) * 4)}
                      className="fill-emerald-500 dark:fill-emerald-400" rx="0.4" />
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between text-[9.5px] text-slate-400 mt-1">
        <span>{fmtDate(data[0].date)}</span>
        <span>{fmtDate(data[data.length - 1].date)}</span>
      </div>
    </div>
  );
};

// ── Leaderboard section ─────────────────────────────────────────
const Leaderboard = ({ title, rows, valueKey = 'loan_amount', icon: Icon }) => {
  const top = rows?.slice(0, 5) || [];
  const max = Math.max(1, ...top.map((r) => r[valueKey] || 0));
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon className="w-4 h-4 text-slate-500" />}
        <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">{title}</h3>
        <span className="ml-auto text-[10.5px] uppercase tracking-widest font-semibold text-slate-400">Top {top.length}</span>
      </div>
      {top.length === 0 ? (
        <div className="text-xs text-slate-400 py-4 text-center">No activity in this period</div>
      ) : (
        <div className="space-y-2.5">
          {top.map((r) => (
            <div key={r.name} data-testid={`leaderboard-row-${r.name}`}>
              <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.name}</span>
                <span className="font-bold tabular-nums text-slate-800 dark:text-slate-100">
                  {formatCompactCurrency(r[valueKey])}
                </span>
              </div>
              <div className="mt-1 h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-300 dark:to-slate-500 rounded-full"
                     style={{ width: `${(r[valueKey] || 0) / max * 100}%` }} />
              </div>
              <div className="flex justify-between text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 tabular-nums">
                <span>{r.lodged} lodged · {r.approved} approved · {r.settled} settled</span>
                <span>{r.conversion_rate?.toFixed(0)}% conv.</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Pipeline breakdown ─────────────────────────────────────────
const PIPELINE_ORDER = [
  { key: 'being_prepared',       label: 'Being prepared',       tone: 'bg-slate-500' },
  { key: 'waiting_customer',     label: 'Awaiting customer docs', tone: 'bg-amber-500' },
  { key: 'ready_for_proposal',   label: 'Ready for proposal',   tone: 'bg-sky-500' },
  { key: 'ready_for_lodgement',  label: 'Ready for lodgement',  tone: 'bg-blue-600' },
  { key: 'waiting_lender',       label: 'In bank (awaiting lender)', tone: 'bg-indigo-500' },
  { key: 'requiring_info',       label: 'Requiring additional info', tone: 'bg-orange-500' },
  { key: 'approved_not_settled', label: 'Approved · awaiting settle', tone: 'bg-emerald-500' },
  { key: 'declined_withdrawn',   label: 'Declined / withdrawn', tone: 'bg-rose-500' },
];
const PipelineBreakdown = ({ pipeline }) => {
  const total = PIPELINE_ORDER.reduce((s, p) => s + (pipeline?.[p.key] || 0), 0);
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-slate-500" />
        <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">Pipeline breakdown</h3>
        <span className="ml-auto text-[10.5px] uppercase tracking-widest font-semibold text-slate-400">
          {total} in play
        </span>
      </div>
      <div className="space-y-2.5">
        {PIPELINE_ORDER.map((row) => {
          const val = pipeline?.[row.key] || 0;
          const pct = total ? (val / total) * 100 : 0;
          return (
            <div key={row.key} data-testid={`pipeline-${row.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-600 dark:text-slate-300">{row.label}</span>
                <span className="tabular-nums font-bold text-slate-900 dark:text-white">{val}</span>
              </div>
              <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all', row.tone)} style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Attention list ────────────────────────────────────────────
const PRIORITY_META = {
  1: { label: 'Critical', tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-800' },
  2: { label: 'Action',   tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-800' },
  3: { label: 'Watch',    tone: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-700' },
};
const AttentionList = ({ items, onClickItem }) => {
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
      <div className="flex items-center gap-2 p-4 pb-3 border-b border-slate-100 dark:border-slate-800">
        <AlertTriangle className="w-4 h-4 text-rose-500" />
        <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">Files requiring attention</h3>
        <span className="ml-auto text-[10.5px] uppercase tracking-widest font-semibold text-slate-400">
          {items.length} file{items.length === 1 ? '' : 's'}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-xs text-slate-400 dark:text-slate-500">
          Everything on track — no files require action.
        </div>
      ) : (
        <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {items.slice(0, 40).map((item) => {
            const meta = PRIORITY_META[item.priority] || PRIORITY_META[3];
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onClickItem && onClickItem(item)}
                data-testid={`attention-row-${item.id}`}
                className="w-full text-left p-3 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors flex items-center gap-3"
              >
                <span className={cn('inline-flex items-center text-[9.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ring-1 ring-inset shrink-0', meta.tone)}>
                  {meta.label}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">{item.client_name || 'Unknown'}</span>
                    <span className="text-[11px] text-slate-500 tabular-nums shrink-0">{formatCompactCurrency(item.loan_amount)}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5">
                    {item.broker || 'Unassigned'} · {item.lender || '—'} · in {item.current_stage} for {item.time_in_stage_days}d
                  </div>
                  <div className="text-[11.5px] text-slate-700 dark:text-slate-200 mt-1">
                    <span className="font-semibold">{item.reason}</span>
                    <span className="text-slate-400 dark:text-slate-500"> · </span>
                    <span className="italic">{item.next_action}</span>
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Recent list ─────────────────────────────────────────────
const RecentList = ({ title, items, tone = 'blue' }) => {
  const toneChip = {
    blue: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  }[tone] || '';
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4">
      <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center">Nothing yet in this period</div>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => (
            <li key={r.id} className="flex items-center gap-2 text-xs">
              <span className={cn('inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap', toneChip)}>
                {fmtDate(r.date)}
              </span>
              <span className="font-semibold text-slate-800 dark:text-slate-100 truncate">{r.client_name}</span>
              <span className="text-slate-400 text-[10.5px] truncate">· {r.broker}</span>
              <span className="ml-auto font-bold tabular-nums text-slate-800 dark:text-slate-100">
                {formatCompactCurrency(r.loan_amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Main dashboard component ────────────────────────────────
const LivePerformanceDashboard = ({ authToken, options, onOpenPrint, onOpenSetGoals }) => {
  const [view, setView] = useState('week');
  const [scope, setScope] = useState('business');
  const [brokerFilter, setBrokerFilter] = useState('');
  const [lenderFilter, setLenderFilter] = useState('');
  const [loanTypeFilter, setLoanTypeFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(() => new Date());
  const [quickEntryOpen, setQuickEntryOpen] = useState(false);

  const fetchData = useCallback(async (silent = false) => {
    if (!authToken) return;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({ view, scope });
      if (brokerFilter)   params.append('broker', brokerFilter);
      if (lenderFilter)   params.append('lender', lenderFilter);
      if (loanTypeFilter) params.append('loan_type', loanTypeFilter);
      const res = await fetch(`${backendUrl}/api/gameplan/live-dashboard?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Live dashboard fetch failed', err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authToken, view, scope, brokerFilter, lenderFilter, loanTypeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!authToken) return undefined;
    const id = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(id);
  }, [authToken, fetchData]);

  const periodLabel = useMemo(() => {
    if (!data?.period) return VIEW_LABEL[view];
    const { from, to } = data.period;
    if (from === to) return `${VIEW_LABEL[view]} · ${fmtDate(from)}`;
    return `${VIEW_LABEL[view]} · ${fmtDate(from)} → ${fmtDate(to)}`;
  }, [data, view]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="live-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const k = data?.kpis || {};
  const proj = data?.projections || {};
  const targets = data?.targets || {};

  return (
    <div className="space-y-5" data-testid="live-performance-dashboard">
      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-3">
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-slate-100 dark:bg-slate-800" data-testid="live-view-toggle">
            {['week', 'month'].map((v) => (
              <button key={v} type="button" onClick={() => setView(v)} data-testid={`live-view-${v}`}
                className={cn('px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                  view === v ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200')}>
                {VIEW_LABEL[v]}
              </button>
            ))}
          </div>
          {/* Scope toggle */}
          <div className="inline-flex items-center gap-0.5 p-0.5 rounded-full bg-slate-100 dark:bg-slate-800" data-testid="live-scope-toggle">
            {Object.entries(SCOPE_LABEL).map(([v, label]) => (
              <button key={v} type="button" onClick={() => setScope(v)} data-testid={`live-scope-${v}`}
                className={cn('px-3 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap',
                  scope === v ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200')}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={brokerFilter || 'all'} onValueChange={(v) => setBrokerFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]" data-testid="live-broker-filter"><Users className="w-3.5 h-3.5 mr-1.5" /><SelectValue placeholder="Broker" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All brokers</SelectItem>
              {(options?.brokers || []).map((b) => <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={loanTypeFilter || 'all'} onValueChange={(v) => setLoanTypeFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px]" data-testid="live-loan-type-filter"><Filter className="w-3.5 h-3.5 mr-1.5" /><SelectValue placeholder="Loan type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All loan types</SelectItem>
              {(options?.loanTypes || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant="default"
            size="sm"
            onClick={() => setQuickEntryOpen(true)}
            data-testid="live-quick-entry-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Quick Entry
          </Button>
          <Button variant="ghost" size="icon" onClick={() => fetchData()} data-testid="live-refresh-btn">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
          {onOpenPrint && (
            <Button variant="outline" size="sm" onClick={onOpenPrint} data-testid="live-print-btn"
              className="border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900">
              <Printer className="w-4 h-4 mr-1.5" /> Print
            </Button>
          )}
        </div>
      </div>

      {/* Period + Live indicator */}
      <div className="flex items-center justify-between flex-wrap gap-2 -my-1">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] font-bold text-slate-500">Performance overview</div>
          <div className="text-lg font-black tracking-tight text-slate-900 dark:text-white mt-0.5">{periodLabel}</div>
        </div>
        <div className="text-[11px] tracking-widest text-slate-500 dark:text-slate-400 font-semibold uppercase flex items-center gap-2">
          <span className="dx-live-dot" /><span className="text-lime-600 dark:text-lime-400 font-bold">Live</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span className="normal-case tracking-normal font-medium">{lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
          <span className="text-slate-300 dark:text-slate-600">·</span>
          <span className="normal-case tracking-normal font-medium">auto-refresh 60s</span>
        </div>
      </div>

      {/* Activity Log strip (Quick Entry counts for the current period) */}
      {(data?.activity?.period?.total > 0 || data?.activity?.today?.total > 0) && (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-r from-emerald-50/70 via-white to-white dark:from-emerald-950/25 dark:via-slate-900 dark:to-slate-900 shadow-sm p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 pr-3 mr-1 border-r border-slate-200/70 dark:border-slate-700/70">
              <Plus className="w-3.5 h-3.5 text-emerald-600" />
              <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-700 dark:text-slate-200">Activity log</span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400 ml-1">
                {data.activity.today.total} today · {data.activity.period.total} in period
              </span>
            </div>
            {QUICK_ENTRY_EVENTS.map((ev) => {
              const periodCount = data.activity.period.by_type?.[ev.key] || 0;
              const todayCount = data.activity.today.by_type?.[ev.key] || 0;
              if (!periodCount && !todayCount) return null;
              const Icon = ev.icon;
              return (
                <span
                  key={ev.key}
                  className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm"
                  data-testid={`activity-strip-${ev.key}`}
                  title={`${todayCount} today · ${periodCount} in period`}
                >
                  <span className={cn('w-4 h-4 rounded-full flex items-center justify-center text-white', ev.tone)}>
                    <Icon className="w-2.5 h-2.5" />
                  </span>
                  <span className="font-semibold text-slate-700 dark:text-slate-200">{ev.short}</span>
                  <span className="text-slate-900 dark:text-white font-bold tabular-nums">{periodCount}</span>
                  {todayCount > 0 && todayCount !== periodCount && (
                    <span className="text-[9.5px] text-emerald-600 dark:text-emerald-400 font-bold uppercase tracking-wider">
                      +{todayCount} today
                    </span>
                  )}
                </span>
              );
            })}
            <button
              type="button"
              onClick={() => setQuickEntryOpen(true)}
              data-testid="activity-strip-add-btn"
              className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 dark:text-emerald-300 dark:hover:text-emerald-100"
            >
              <Plus className="w-3 h-3" /> Log new
            </button>
          </div>
        </div>
      )}

      {/* KPI grid removed — the Goal Progress widget below now surfaces
          all these metrics (with targets, remaining and pace-based glow). */}

      {/* Goals progress vs Set Goals funnel */}
      <GoalsProgressWidget goals={data?.goals} view={view} onOpenSetGoals={onOpenSetGoals} />

      {/* Turnaround strip */}
      {(data?.turnaround?.pickup_to_lodgement != null
        || data?.turnaround?.lodgement_to_approval != null
        || data?.turnaround?.approval_to_settlement != null) && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          {[
            { key: 'pickup_to_lodgement',   label: 'Pickup → lodgement' },
            { key: 'lodgement_to_approval', label: 'Lodgement → approval' },
            { key: 'approval_to_settlement', label: 'Approval → settlement' },
          ].map((t) => (
            <div key={t.key} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm">
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400">{t.label}</div>
              <div className="mt-1 text-xl font-black tabular-nums text-slate-900 dark:text-white">
                {data?.turnaround?.[t.key] != null ? `${data.turnaround[t.key]} days` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Middle row: Pipeline breakdown + Trend + Recent lodged */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <PipelineBreakdown pipeline={data?.pipeline} />
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calendar className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">Daily activity</h3>
            <span className="ml-auto text-[10.5px] uppercase tracking-widest font-semibold text-slate-400">Lodged / approved</span>
          </div>
          <TrendChart data={data?.trend || []} />
          <div className="flex items-center gap-3 mt-3 text-[10.5px] text-slate-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-sky-500" /> Lodged</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Approved</span>
          </div>
        </div>
        <RecentList title="Recently lodged" items={data?.recent?.lodged || []} tone="blue" />
      </div>

      {/* Attention list — full width */}
      <AttentionList items={data?.attention || []} />

      {/* Leaderboards row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Leaderboard title="Broker performance" rows={data?.leaderboards?.brokers} icon={Users} />
        <Leaderboard title="Lender performance" rows={data?.leaderboards?.lenders} icon={Activity} />
        <Leaderboard title="Credit team" rows={data?.leaderboards?.credit_team} icon={Trophy} />
      </div>

      {/* Recent approved */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RecentList title="Recently approved" items={data?.recent?.approved || []} tone="emerald" />
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-4">
          <h3 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white mb-3">This period at a glance</h3>
          <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1.5">
            <li>• {fmtInt(k.new_files?.count)} new files picked up ({formatCurrency(k.new_files?.amount)})</li>
            <li>• {fmtInt(k.lodgements?.count)} applications lodged ({formatCurrency(k.lodgements?.amount)}) — {formatCurrency(k.revenue?.lodged)} est. revenue</li>
            <li>• {fmtInt(k.approvals?.count)} approvals ({formatCurrency(k.approvals?.amount)})</li>
            <li>• {fmtInt(k.settlements?.count)} settlements ({formatCurrency(k.settlements?.amount)}) — {formatCurrency(k.revenue?.settled)} est. revenue</li>
            <li>• {(data?.attention || []).length} file{(data?.attention || []).length === 1 ? '' : 's'} needing action</li>
            {proj.projected_volume != null && (
              <li>• Projected month-end: <span className="font-semibold">{formatCurrency(proj.projected_volume)}</span> ({proj.projected_lodgements} lodgements)</li>
            )}
          </ul>
        </div>
      </div>

      {/* Quick Entry dialog */}
      <QuickEntryDialog
        open={quickEntryOpen}
        onOpenChange={setQuickEntryOpen}
        authToken={authToken}
        onSaved={() => fetchData(true)}
        todayCounts={data?.activity?.today?.by_type || {}}
      />
    </div>
  );
};

export default LivePerformanceDashboard;
export { LivePerformanceDashboard };
